// ============================================================
// TERROR TCG — RULE ADAPTER
// ============================================================
// THE SEAL'S KEYSTONE.
//
// RulesEnforcer.js speaks a different dialect than Shatterfield.js:
//   • Enforcer expects slot.level / slot.isDeviant / slot.isFallen / slot.lyf
//   • Shatterfield writes  slot.escendant(id) / slot.deviant / slot.fallen / slot.currentLyf
//   • Enforcer's soul prefix check used "a_" for Affinities — real IDs are "aff_"
//   • Enforcer's combat math uses RAW card stats — the board uses EFFECTIVE
//     stats (Diamond Skin, Cosmic Casbah, Gauntlets, Z4Z, Premier Dragon…)
//
// This module is the ONLY place those two dialects meet. It normalizes a
// live Shatterfield slot into the shape the Enforcer validates, resolving
// effective Lyt/Lyf the exact same way the board does, so a rule check can
// NEVER disagree with what the player sees.
//
// If a rule changes, it changes in rules.js. If a stat buff changes, it
// changes in the effective-stat resolvers passed in here. Neither the
// Enforcer nor Shatterfield re-implements the other. One truth, one gate.
// ============================================================

import { ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS } from "./cards";
import { baseId } from "./necroSeal";

const ALL_CARDS = [
  ...ESCENDANTS, ...TOMES, ...IMPERA, ...UHLTIS,
  ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS,
];

// THE SEAL IS NOT PART OF THE NAME. A ruined being enters the
// Necropolis sealed with a per-copy identity (necroSeal.js - R85), so a
// resurrected Escendant sits in its rift as "spectral_tiger_champion~0~9",
// not "spectral_tiger_champion". This lookup matched the id EXACTLY, so
// every normalised audit read a slot holding a resurrected being as an
// EMPTY SLOT - and the referee duly reported a Primera press on a being
// that was plainly standing there (P04, 23 times in 120 games, every one
// a false positive). GameStateValidator's own cardOf already stripped;
// this one did not. One strip, in the one place that turns raw state
// into rule state.
//
// ⚠ ORDERING IS LOAD-BEARING: the EXACT id is tried first and the
// stripped one only as a fallback, so a catalogue card whose own id ever
// contained the seal separator would still answer to itself rather than
// to its prefix. Indexed rather than scanned because this is now on the
// hot path of every audit of every slot of every turn — a sealed id used
// to cost two full linear scans of the catalogue. First-wins on a
// duplicate id, exactly as Array.find behaved.
const BY_ID = new Map();
for (const c of ALL_CARDS) if (c && c.id && !BY_ID.has(c.id)) BY_ID.set(c.id, c);

export const cardById = (id) => {
  if (!id) return null;
  return BY_ID.get(id) || BY_ID.get(baseId(id)) || null;
};

export const allCardsFlat = () => ALL_CARDS;

// ── SOUL PREFIX HELPERS (correct, real-ID-aware) ─────────────
// Real prefixes per cards.js: u_ (U'hlti), aff_ (Affinity), p_ (Impera),
// ht_ (Heretical Tome). The old enforcer used "a_" — which matched NOTHING.
const soulHas = (slot, predicate) =>
  (slot?.soul || []).some((entry) => {
    const id = typeof entry === "string" ? entry : entry?.id;
    return id ? predicate(id) : false;
  });

export const slotHasUhlti = (slot) => soulHas(slot, (id) => id.startsWith("u_"));
export const slotHasAffinity = (slot) => soulHas(slot, (id) => id.startsWith("aff_"));
export const slotHasHereticalTome = (slot) => soulHas(slot, (id) => id.startsWith("ht_"));

// ── SLOT NORMALIZATION ───────────────────────────────────────
// Turn a live Shatterfield escling slot into the shape RulesEnforcer reads.
// `effLyf` is the effective-Lyf resolver from Shatterfield (getEffectiveLyfPure);
// passing it keeps combat math identical between gate and board. If omitted,
// falls back to currentLyf / base.
export const normalizeSlot = (slot, myField = null, effLyf = null) => {
  if (!slot) return null;
  const card = cardById(slot.escendant);

  // Esclings: escendant is null but an escling id is present.
  if (!slot.escendant && slot.escling) {
    const eCard = cardById(slot.escling);
    return {
      __raw: slot,
      escling: true,
      escendant: null,
      level: 0,
      school: eCard?.school ?? null,
      ilk: eCard?.ilk ?? null,
      isDeviant: false,
      isFallen: false,
      lyf: 0,
      // R29 is about ESCENDANTS. An Escling has no Lyf to lose, so it is
      // never "Fractured" — see THE FRACTURE DERIVATION below.
      isFractured: false,
      soul: [],
    };
  }

  if (!card) {
    // Empty slot.
    return { __raw: slot, escling: false, escendant: null, level: 0, lyf: 0, isFractured: false, soul: [] };
  }

  const resolvedLyf =
    typeof effLyf === "function"
      ? effLyf(slot, myField)
      : slot.currentLyf !== null && slot.currentLyf !== undefined
        ? slot.currentLyf
        : (card.lyf ?? 0);

  return {
    __raw: slot,
    escling: false,
    escendant: slot.escendant,
    id: slot.escendant,
    name: card.name,
    type: card.type,
    level: card.level ?? 1,
    school: card.school ?? null,
    ilk: card.ilk ?? null,
    // ── NATURAL vs DEVIANT — BOTH FIELDS, AND THE STRICT DIRECTION ──
    // Shatterfield writes the two as a PAIR (`deviant: false, natural:
    // true`) and has a banner of its own about keeping them in step. The
    // board's own U'hlti guard asks `!slot.natural`, which reads an
    // ABSENT field as Deviant; this adapter asked only `!!slot.deviant`,
    // which reads an absent field as Natural. So on any slot that
    // carried one word and not the other, the gate was the MORE
    // PERMISSIVE of the two — the one direction a gate must never be.
    //
    // Both are read now, and a POSITIVE Deviant signal from either field
    // wins. Absent-both still reads as Natural, which is the same
    // compatibility hinge `phaseOf` uses: every stored duel and every
    // headless fixture validates exactly as it did.
    isDeviant: !!slot.deviant || slot.natural === false,
    natural: !(!!slot.deviant || slot.natural === false),
    // Shatterfield uses `fallen`; enforcer reads `isFallen`.
    isFallen: !!slot.fallen,
    lyf: resolvedLyf,
    // ── THE FRACTURE DERIVATION, IN ONE PLACE (R29) ─────────────
    // R29: "0-Lyf Escendants are ALWAYS Fractured". Fracture is not a
    // field — no code path in Terror writes `slot.isFractured`, and none
    // should: Shatterfield derives it at render time
    // (`const fractured = lyf === 0`, inside its FieldCard renderer) from
    // the very same effective Lyf resolved above. ⚠ CITED BY FUNCTION AND
    // NOT BY LINE: this comment named `Shatterfield.js:869` for months,
    // and line 869 is a coordinate-clamp helper 300 lines short of the
    // derivation. A line number is a citation that rots the next time
    // anybody inserts a function above it. Emitting it here means a
    // consumer of the normalised shape never has to re-derive it, and can
    // never derive it differently from the board.
    //
    // ⚠ AND THEREFORE IT IS NOT CHECKABLE. Because this is a DERIVATION
    // and not a stored fact, `isFractured !== (lyf === 0)` is unfalsifiable
    // by construction: there is no second writer to disagree with. Any
    // "check" comparing the two is vacuous, which is exactly why
    // GameStateValidator's F10/F11 were withdrawn and R29 now sits in
    // UNREACHABLE_FROM_SNAPSHOT. Do not re-add one.
    isFractured: resolvedLyf === 0,
    soul: slot.soul || [],
    // 🔥 R68 — THE HERETICAL TOME IS NOT IN THE SOUL. Shatterfield keeps
    // it on its own field precisely so that everything reading `soul`
    // (descent's keptSoul, every Resurrect path, the soul pip) ignores
    // it for free. The Enforcer has to be told about it explicitly for
    // the same reason, or R72's one-thing-in-the-U'hlti-slot rule
    // cannot see the thing that is in the slot.
    heretical: slot.heretical || null,
    resurrectionsUsed: slot.resurrectionsUsed || 0,
  };
};

// ── FIELD NORMALIZATION ──────────────────────────────────────
// RulesEnforcer reads field.esclings[].{escling,escendant,isFallen,...}.
// Normalize every slot so isEmpty/isFallen/getBeingLevel work correctly.
export const normalizeField = (field, effLyf = null) => {
  if (!field) return { esclings: [], tomes: [], curios: [] };
  const esclings = (field.esclings || []).map((s) => normalizeSlot(s, field, effLyf));
  return {
    esclings,
    tomes: field.tomes || [],
    curios: field.curios || [],
    prophecy: field.prophecy || null,
  };
};

// ── PLAYER / GAMESTATE NORMALIZATION ─────────────────────────
// validateAction reads gameState.me / gameState.opponent with
// {khao, anima, field, grimoire, necropolis, hand}. Shatterfield stores
// grimoire as a {id: qty} map; normalize length for size checks.
const grimoireLength = (grimoire) => {
  if (Array.isArray(grimoire)) return grimoire.length;
  if (grimoire && typeof grimoire === "object")
    return Object.values(grimoire).reduce((a, b) => a + (b || 0), 0);
  return 0;
};

export const normalizePlayer = (player, effLyf = null) => {
  if (!player) return null;
  return {
    // ── R108 · THE PHASE, CARRIED THROUGH (2026-08-26) ──────────
    // The enforcer asks `phaseOf(gameState.me)` before every action, and
    // `phaseOf` reads an ABSENT phase as MAIN — which is the whole
    // compatibility story. Passing the raw value straight through (not
    // defaulting it here) keeps that single default in rules.js, where
    // the rest of the phase machine lives, instead of giving this file
    // a second opinion about what "no phase" means.
    phase: player.phase,
    khao: player.khao ?? 0,
    anima: player.anima ?? 0,
    maxKhao: player.maxKhao ?? 3,
    hand: player.hand || [],
    grimoire: player.grimoire || {},
    grimoireCount: grimoireLength(player.grimoire),
    necropolis: player.necropolis || [],
    field: normalizeField(player.field, effLyf),
    luxmoire: player.luxmoire || [],
    // ── R112/R113 · THE EXILE RIFT AND THE MARK ────────────────
    // ⚠ THESE TWO WERE DROPPED HERE, AND DROPPING THEM SILENTLY
    // DISARMED R113. `validateExileRetrieval` reads the Mark ledger off
    // `gameState.me.exileReturned`, and this function is what builds
    // `gameState.me` — so the ledger arrived as `undefined`, defaulted
    // to `[]`, and "once ever, per card" was a rule that could never
    // refuse anything. The rift itself had the same hole. Both are
    // carried now, and they are carried RAW: the Exile rift is a list of
    // ids and the ledger is a list of ids, and neither has a shape this
    // adapter is entitled to an opinion about.
    exile: player.exile || [],
    exileReturned: player.exileReturned || [],
    // ── R115/R119 · THE ART CLOCKS, CARRIED RAW ────────────────
    // ⚠ THESE WERE DROPPED, AND DROPPING THEM MADE A GUARD UNREACHABLE
    // IN THE SAME WAY exileReturned's absence disarmed the Mark.
    // validateAction's ROLE_ART case reads
    // `action.wardenArtsThisTurn ?? gameState.me?.wardenArtsThisTurn ?? 0`
    // and this function is what builds `gameState.me` — so the fallback
    // resolved to `undefined` and then to 0 on every single ask, which
    // means R119's one-Art-per-Turn cap had exactly one supplier and no
    // backstop. Both clocks are carried now.
    //
    // ⚠ AND THEY ARE CARRIED RAW, ON PURPOSE. `wardenArtsThisTurn` sits
    // on the seat as `{ turn, n }` and `roleArtUsed` as
    // `{ [riftIdx]: halfTurn }`. Resolving either here would put the
    // FULL-turn-versus-half-turn arithmetic in an adapter, and that unit
    // belongs to the rule: RulesEnforcer.wardenArtsSpentThisFullTurn
    // owns it, reads the stamp, and is the only place the window is
    // decided. An adapter that had an opinion about it would be the
    // second opinion this file exists to prevent.
    wardenArtsThisTurn: player.wardenArtsThisTurn,
    roleArtUsed: player.roleArtUsed,
  };
};

// ── THE TURN BASE, OWNED HERE ────────────────────────────────
// ⚠ EVERY CITATION IN THIS BLOCK NAMED A LINE NUMBER AND EVERY ONE OF
// THEM HAD ROTTED — `RulesEnforcer.js:414` landed in the middle of the
// deck-limit table, `:499` in a comment, `gameManager.js:172` in a
// docstring about AI decks. They are cited by FUNCTION NAME now, which
// is the only citation format in a 2,500-line file that survives an
// edit above it. A comment is a claim and a claim must be checkable.
//
// `RulesEnforcer.isOpeningTurn` carries a note asking this module to own
// the turn dialect, because Shatterfield and gameManager count
// `match.turn` in ZERO-BASED HALF-TURNS (`gameManager.createMatch`
// writes `turn: 0`) while the enforcer's docstrings were written for a
// 1-based turn number.
//
// ⚠ OWNING IT IS NOT SHIFTING IT, and the +1 is a TRAP. The enforcer's
// live predicates are already correct in the ZERO-BASED half-turn base
// and only in that base:
//   · `isOpeningTurn` is `t < openingCycleLength(seatCount)` — the
//     opening CYCLE is each seat's first Turn, so half-turns 0 and 1 at
//     a duel and 0..3 at a 2v2. (This comment used to say the predicate
//     was `t <= 1`; it was rewritten seat-count-aware on 2026-08-25 and
//     the comment did not move with it.) Add one to the turn and
//     half-turn 1 becomes 2: the SECOND seat loses its opening-Turn
//     restrictions entirely.
//   · `validateKhaoRefill` gates R91 on `turn < FIRST_KHAO_TURN` (2) —
//     the same window, and it breaks the same way.
// So the adapter's job here is to fix the DIALECT, not to renumber it:
// coerce to a finite, non-negative half-turn integer (a string "3", a
// missing field or a NaN from a half-written match must never reach the
// enforcer as a turn), publish it under the name the enforcer reads, and
// publish the 1-based turn NUMBER alongside for anything that genuinely
// wants to display or reason in that dialect. `turn` stays zero-based
// half-turns, so the enforcer's defensive `t <= 1` keeps working
// unchanged and cannot be wrong in either base.
const normalizeTurn = (raw) => {
  const t = Math.floor(Number(raw));
  return Number.isFinite(t) && t >= 0 ? t : 0;
};

export const normalizeGameState = (me, opponent, extra = {}, effLyf = null) => ({
  ascensionsThisTurn: extra.ascensionsThisTurn ?? 0,
  defendersAttackedThisTurn: extra.defendersAttackedThisTurn ?? [],
  ...extra,
  me: normalizePlayer(me, effLyf),
  opponent: normalizePlayer(opponent, effLyf),
  // ⚠ AFTER the spread, deliberately. `...extra` used to land on top of
  // `turn: extra.turn ?? 0`, so the raw, uncoerced value won every time
  // and the normalization above it was decorative. The owned value is
  // written last so it is the one that survives.
  turn: normalizeTurn(extra.turn),
  // The same instant in the enforcer's documented dialect: half-turn 0
  // is Turn 1. Nothing in the enforcer reads this yet — it exists so the
  // next person who needs "the turn number" does not reach for `turn`
  // and add one to it.
  turnNumber: normalizeTurn(extra.turn) + 1,
  turnBase: "half-turns, zero-based (match.turn)",
  // ── THE SEAT COUNT, OWNED HERE TOO (2026-08-25) ────────────
  // R08's window is THE OPENING CYCLE — each seat's first Turn — so
  // every predicate that asks "is this the opening Turn" needs to know
  // how many seats there are. Defaulted to 2 in exactly one place, here,
  // rather than at the eight call sites that would otherwise each have
  // to remember `|| 2`. A 1v1 is 2 and every enforcer predicate reduces
  // to the `t <= 1` it has always been.
  seatCount: Math.max(2, Math.floor(Number(extra.seatCount)) || 2),
});
